import { Extension } from './extension'
import type { OAuth2Props } from './oauth/types'
import type { DictionaryRef } from './params'
import type { PollingTriggerProps } from './polling-triggers/types'
import type { RealtimeSetupProps, RealtimeTriggerProps } from './realtime-triggers/types'
import { registry } from './registry'
import type {
  ActionProps,
  AnySchema,
  ApiRequestFn,
  DictionaryProps,
  ExtensionApi,
  ExtensionProps,
  ShapeOf,
} from './types'

// `Flowrunner.createExtension(props)` — the v2 factory. Builds the Extension, registers it, and returns the
// `ext` handle: the authoring-facing façade whose registrars delegate straight to the Extension, which owns
// the validation and the runtime-generated loader methods (see ./extension.ts).

// Generic so an author's own types reach their handlers: `configItems` / `initContext` / `helpers` /
// `apiRequest` are inferred here once, and the returned `ext` threads them into every registrar (which
// then infers its own params). The body works against the erased props, since none of that matters to the
// runtime — the single cast on the way out is where the author's shape is re-attached.
export function createExtension<
  TConfigSchema extends AnySchema = AnySchema,
  TContext = Record<string, unknown>,
  THelpers = Record<string, unknown>,
  TApiRequest extends ApiRequestFn = ApiRequestFn,
>(
  props: ExtensionProps<TConfigSchema, TContext, THelpers, TApiRequest>
): ExtensionApi<ShapeOf<TConfigSchema, TContext, THelpers, TApiRequest>> {
  const extension = new Extension(props as unknown as ExtensionProps)

  const api = {
    dictionary(dictionaryProps: DictionaryProps): DictionaryRef {
      return extension.registerDictionary(dictionaryProps)
    },

    addAction(actionProps: ActionProps): void {
      extension.registerAction(actionProps)
    },

    addPollingTrigger(triggerProps: PollingTriggerProps): void {
      extension.registerPollingTrigger(triggerProps)
    },

    setupRealtimeTriggers(realtimeProps: RealtimeSetupProps): void {
      extension.setupRealtime(realtimeProps)
    },

    addRealtimeTrigger(triggerProps: RealtimeTriggerProps): void {
      extension.registerRealtimeTrigger(triggerProps)
    },

    setupOauth2(oauthProps: OAuth2Props): void {
      extension.setupOAuth2(oauthProps)
    },

    extension,
  }

  registry.register(extension)

  return api as unknown as ExtensionApi<ShapeOf<TConfigSchema, TContext, THelpers, TApiRequest>>
}
